import { Center, Divider } from "@chakra-ui/layout";
import { Box, Button, Flex, Icon, Modal, Spinner, Text, useTranslation } from "@familyzone/component-library";
import { Tag, TagLabel } from "@chakra-ui/tag";
import React from "react";
import {
  StudentActionEvent,
  StudentActionType,
  Guardian,
  Student,
  isLocalGuardian,
  RestrictionType,
  isRestrictionForStudent,
} from "../../types/Community";

interface Props {
  open: boolean;
  guardian: Guardian;
  students?: Student[];
  policyDelegationEnabled: boolean;
  onClose: () => void;
  onStudentAction: (event: StudentActionEvent) => void;
  onEdit: (guardian: Guardian) => void;
}

const ClaimedStudent: React.FC<{
  student: Student;
  policyDelegationEnabled: boolean;
  onRestrictClaimed: () => void;
}> = ({ student, policyDelegationEnabled, onRestrictClaimed }) => {
  const { t } = useTranslation();

  return (
    <Flex py="sp8" px="sp12" backgroundColor="neutrals.0" borderRadius="sm" alignItems="center">
      <Box flexGrow={1}>
        <Text fontFamily="Roboto" fontSize="md" fontWeight="medium" color="text.title">
          {student.firstName} {student.middleName ?? ""} {student.lastName}
          {policyDelegationEnabled && student.delegated && (
            <Tag variant="outline" backgroundColor="brand.200" color="brand.500" size="compact" ml={6} display={"inline"}>
              <TagLabel>{t("Extended Rules Accepted")}</TagLabel>
            </Tag>
          )}
        </Text>

        <Text fontFamily="Roboto" fontSize="md" fontWeight="normal" color="text.paragraph.regular">
          {student.email ?? student.username}
        </Text>
      </Box>
      <Box alignItems="center">
        <Button
          size="compact"
          variant="primary"
          onClick={onRestrictClaimed}
          leftIcon={<Icon icon="fa-link-slash" />}
          data-testid="restrict-claimed"
        >
          {t("Restrict Student")}
        </Button>
      </Box>
    </Flex>
  );
};

const UnclaimedStudent: React.FC<{
  student: Student;
  disabled?: boolean;
  policyDelegationEnabled: boolean;
  onRestrictLinked: () => void;
}> = ({ student, disabled, policyDelegationEnabled, onRestrictLinked }) => {
  const { t } = useTranslation();

  return (
    <Flex py="sp8" px="sp12" backgroundColor="neutrals.0" borderRadius="sm" alignItems="center">
      <Box flexGrow={1}>
        <Text fontFamily="Roboto" fontSize="md" fontWeight="medium" color="text.title">
          {student.firstName} {student.middleName ?? ""} {student.lastName}
          {policyDelegationEnabled && student.delegated && (
            <Tag variant="outline" backgroundColor="brand.200" color="brand.500" size="compact" ml={6}>
              <TagLabel>{t("Extended Rules Accepted")}</TagLabel>
            </Tag>
          )}
        </Text>

        <Text fontFamily="Roboto" fontSize="md" fontWeight="normal" color="text.paragraph.regular">
          {student.email ?? student.username}
        </Text>
      </Box>
      <Flex direction="row" alignItems="center">
        <Button
          size="compact"
          variant="primary"
          onClick={onRestrictLinked}
          leftIcon={<Icon icon="fa-link-slash" />}
          disabled={disabled}
          data-testid="restrict-unclaimed"
        >
          {t("Restrict Student")}
        </Button>
      </Flex>
    </Flex>
  );
};

const RestrictedStudent: React.FC<{
  student: Student;
  disabled?: boolean;
  onUnRestrict: () => void;
}> = ({ student, disabled, onUnRestrict }) => {
  const { t } = useTranslation();

  return (
    <Flex py="sp8" px="sp12" backgroundColor="neutrals.0" borderRadius="sm" alignItems="center">
      <Box flexGrow={1}>
        <Text fontFamily="Roboto" fontSize="md" fontWeight="medium" color="text.title">
          {student.firstName} {student.middleName ?? ""} {student.lastName}
        </Text>

        <Text fontFamily="Roboto" fontSize="md" fontWeight="normal" color="text.paragraph.regular">
          {student.email ?? student.username}
        </Text>
      </Box>
      <Flex direction="row" alignItems="center">
        <Button size="compact" variant="primary" onClick={onUnRestrict} leftIcon={<Icon icon="fa-link" />} disabled={disabled}>
          {t("Unrestrict Student")}
        </Button>
      </Flex>
    </Flex>
  );
};

const ParentDetailsModal: React.FC<Props> = ({
  guardian,
  students,
  open,
  policyDelegationEnabled,
  onClose,
  onStudentAction,
  onEdit,
}: Props) => {
  const { t } = useTranslation();

  const handleRestrictClaimed = (guardian: Guardian, student: Student): void => {
    onStudentAction({ guardian, student, type: StudentActionType.DisconnectStudentFromParent });
  };

  const handleRestrictLinked = (guardian: Guardian, student: Student): void => {
    onStudentAction({ guardian, student, type: StudentActionType.UnlinkStudentFromParent });
  };

  const handleUnRestrict = (guardian: Guardian, student: Student): void => {
    onStudentAction({ guardian, student, type: StudentActionType.UnRestrictStudent });
  };

  const getRestrictedStudents = (guardian: Guardian, students: Student[]): Student[] => {
    if (guardian.restriction?.type === RestrictionType.GuardianClaimGlobalRestriction) {
      return students;
    } else if (guardian.restriction?.type === RestrictionType.GuardianClaimUserListRestriction) {
      return students.filter((s) => guardian.restriction?.userListValues.some((r) => isRestrictionForStudent(r, s)));
    }
    return [];
  };

  const getUnclaimedStudents = (guardian: Guardian, students: Student[]): Student[] => {
    if (guardian.restriction?.type === RestrictionType.GuardianClaimGlobalRestriction) {
      return [];
    } else if (guardian.restriction?.type === RestrictionType.GuardianClaimUserListRestriction) {
      return students.filter((s) => !s.claimed && !guardian.restriction?.userListValues.some((r) => isRestrictionForStudent(r, s)));
    }
    return students.filter((s) => !s.claimed);
  };

  const claimedStudents = students ? students.filter((s) => s.claimed) : [];
  const unclaimedStudents = getUnclaimedStudents(guardian, students ?? []);
  const restrictedStudents = getRestrictedStudents(guardian, students ?? []);

  const localGuardian = isLocalGuardian(guardian);
  const canEdit = localGuardian && claimedStudents.length === 0 && restrictedStudents.length === 0;

  return (
    <Modal isOpen={open} onClose={onClose} size="md">
      <Flex mb="sp24">
        <Flex flexDirection="column" flexGrow={1}>
          <Text as="h6" fontFamily="Poppins" fontSize="xs" fontWeight="medium" color="text.paragraph.regular" textTransform="uppercase">
            {t("Parent")}
          </Text>
          <Text as="h2" fontFamily="Poppins" fontSize="lg" fontWeight="medium" color="text.title">
            {guardian.firstName} {guardian.lastName}
          </Text>
          <Text fontFamily="Roboto" fontSize="sm" fontWeight="normal" color="text.paragraph.regular">
            {guardian.email}
          </Text>
        </Flex>
        <Flex alignItems="center">
          <Button
            size="compact"
            variant="outline"
            onClick={() => onEdit(guardian)}
            leftIcon={<Icon icon="fa-pencil" />}
            disabled={!canEdit}
          >
            {t("Edit Parent Details")}
          </Button>
        </Flex>
      </Flex>

      <Flex mb="sp16" color="text.title">
        <Center width="20px" height="20px" borderWidth="2px" borderRadius="100px" borderColor="brand.400">
          <Text fontSize="sm" fontWeight="semibold" lineHeight="18px">
            {students?.length ?? 0}
          </Text>
        </Center>
        <Text ml="sp8">Total Students</Text>
        <Center ml="sp24" width="20px" height="20px" borderWidth="0px" borderRadius="100px" backgroundColor="accent.turquoise.100">
          <Text fontSize="sm" fontWeight="semibold" lineHeight="20px">
            {claimedStudents.length}
          </Text>
        </Center>
        <Text ml="sp8">Connected</Text>
        <Center ml="sp24" width="20px" height="20px" borderWidth="0px" borderRadius="100px" backgroundColor="neutrals.20">
          <Text fontSize="sm" fontWeight="semibold" lineHeight="20px">
            {unclaimedStudents.length}
          </Text>
        </Center>
        <Text ml="sp8">Assigned</Text>
        <Center ml="sp24" width="20px" height="20px" borderWidth="0px" borderRadius="100px" backgroundColor="accent.orange.100">
          <Text fontSize="sm" fontWeight="semibold" lineHeight="20px">
            {restrictedStudents.length}
          </Text>
        </Center>
        <Text ml="sp8">Restricted</Text>
      </Flex>

      <Divider borderColor="neutrals.40" />

      <Flex mt="sp12" flexDirection="column" minHeight="100px" gap="sp12" overflowY="auto">
        {students === undefined && (
          <Flex flexGrow={1} alignItems="center" justifyContent="center" data-testid="spinner">
            <Spinner />
          </Flex>
        )}

        {claimedStudents.length > 0 && (
          <Box p="sp16" backgroundColor="#E3F8FF" borderRadius="sm" data-testid="claimed-students-section">
            <Text fontFamily="Roboto" fontSize="md" fontWeight="medium" color="text.title">
              {t("Connected Students")}
            </Text>
            <Text fontFamily="Roboto" fontSize="sm" fontWeight="normal" color="text.paragraph.light">
              {t("Students listed below have been connected to this parent through Qustodio app.")}
            </Text>

            <Flex gap="sp4" mt="sp12" flexDirection="column">
              {claimedStudents.map((s) => (
                <ClaimedStudent
                  key={s.id}
                  student={s}
                  policyDelegationEnabled={policyDelegationEnabled}
                  onRestrictClaimed={() => handleRestrictClaimed(guardian, s)}
                />
              ))}
            </Flex>
          </Box>
        )}

        {unclaimedStudents.length > 0 && (
          <Box p="sp16" backgroundColor="neutrals.20" borderRadius="sm" data-testid="unclaimed-students-section">
            <Text fontFamily="Roboto" fontSize="md" fontWeight="bold" color="text.title">
              {t("Assigned Students")}
            </Text>
            <Text fontFamily="Roboto" fontSize="sm" fontWeight="normal" color="text.paragraph.light">
              {t(
                "Students listed below have been assigned to this parent, but are yet to be connected by the parent through the Qustodio App."
              )}
            </Text>

            <Flex gap="sp4" mt="sp12" flexDirection="column">
              {unclaimedStudents.map((s) => (
                <UnclaimedStudent
                  key={s.id}
                  student={s}
                  policyDelegationEnabled={policyDelegationEnabled}
                  disabled={false}
                  onRestrictLinked={() => handleRestrictLinked(guardian, s)}
                />
              ))}
            </Flex>
          </Box>
        )}

        {restrictedStudents.length > 0 && (
          <Box p="sp16" backgroundColor="accent.orange.100" borderRadius="sm" data-testid="restricted-students-section">
            <Text fontFamily="Roboto" fontSize="md" fontWeight="bold" color="text.title">
              {t("Restricted Students")}
            </Text>
            <Text fontFamily="Roboto" fontSize="sm" fontWeight="normal" color="text.paragraph.light">
              {t("Students listed below have been restricted and can no longer be connected by the parent in the Qustodio app.")}
            </Text>

            <Flex gap="sp4" mt="sp12" flexDirection="column">
              {restrictedStudents.map((s) => (
                <RestrictedStudent key={s.id} student={s} disabled={false} onUnRestrict={() => handleUnRestrict(guardian, s)} />
              ))}
            </Flex>
          </Box>
        )}
      </Flex>
    </Modal>
  );
};

export default ParentDetailsModal;
